import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { refundProject } from '../services/adminService';
import LoadingSpinner from './LoadingSpinner';

interface Props {
  isOpen: boolean;
  projectId: string;
  projectName: string;
  userEmail: string;
  amountCents: number;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RefundProjectModal: React.FC<Props> = ({
  isOpen,
  projectId,
  projectName,
  userEmail,
  amountCents,
  currency,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const amountFormatted = `${currency.toUpperCase()} $${(amountCents).toFixed(2)}`;

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await refundProject(projectId);
      onSuccess();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Refund failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <FaExclamationTriangle className="w-5 h-5 text-red-600" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Refund Project</h2>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-xl text-sm space-y-1">
          <p className="text-gray-500">
            Project: <span className="font-semibold text-gray-900">{projectName}</span>
          </p>
          <p className="text-gray-500">
            User: <span className="font-semibold text-gray-900">{userEmail}</span>
          </p>
          <p className="text-gray-500">
            Refund amount: <span className="font-semibold text-gray-900">{amountFormatted}</span>
          </p>
        </div>

        <p className="text-sm text-gray-600 mb-5">
          This will immediately issue a Stripe refund and{' '}
          <span className="font-semibold text-red-600">permanently delete the project</span>.
          The user will receive a refund confirmation email and a project deletion notice.
          This action cannot be undone.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading && <LoadingSpinner size="sm" color="white" />}
            {loading ? 'Processing...' : 'Confirm Refund'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundProjectModal;
